// Overlay persistence (client). The in-game editor's changes live here so they
// survive a reload, and export as an archive that drops straight into the repo.
// Kept in localStorage rather than IndexedDB: an overlay is a small sparse patch,
// and the registries load synchronously at boot, before any async store is open.
import { emptyOverlay, type Overlay, type KindPatch } from '../../sim/registry/overlay';

const KEY = 'tf.overlay';

/** Read the saved overlay. Never throws — a corrupt or blocked store just means
 *  the player gets shipped content, which is the correct degradation. */
export function loadOverlay(): Overlay {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyOverlay();
    const parsed = JSON.parse(raw) as Overlay;
    if (!parsed || typeof parsed !== 'object' || !parsed.patches) return emptyOverlay();
    return {
      collection: parsed.collection ?? 'default',
      version: parsed.version ?? '0.1',
      patches: migrate(parsed.patches),
    };
  } catch {
    return emptyOverlay();
  }
}

export function saveOverlay(o: Overlay): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(o));
  } catch {
    // Private window / quota: the edit still applies for this session.
  }
}

export function clearOverlay(): void {
  try {
    localStorage.removeItem(KEY);
  } catch { /* nothing to do */ }
}

/** Upgrade overlays written by an older build.
 *
 *  Block and rig-part textures were once a single icon applied to every face;
 *  they are now per-face. An un-migrated overlay fails schema validation, and
 *  the loader would (correctly) fall back to shipped content — silently losing
 *  the author's edits. Rewriting the old shape as `{ all: … }` preserves them. */
function migrate(patches: Overlay['patches']): Overlay['patches'] {
  const isFlatIcon = (v: unknown): boolean =>
    typeof v === 'object' && v !== null && 'pixels' in v && 'palette' in v;

  const out = { ...patches };
  const blocks = out.blocks;
  if (blocks) {
    const next: KindPatch = {};
    for (const [name, fields] of Object.entries(blocks)) {
      const tex = fields.texture;
      next[name] = isFlatIcon(tex) ? { ...fields, texture: { all: tex } } : fields;
    }
    out.blocks = next;
  }
  const mobs = out.mobs;
  if (mobs) {
    const next: KindPatch = {};
    for (const [name, fields] of Object.entries(mobs)) {
      const rig = fields.rig;
      next[name] = Array.isArray(rig)
        ? {
          ...fields,
          rig: rig.map((part) => {
            const p = part as Record<string, unknown>;
            return isFlatIcon(p.texture) ? { ...p, texture: { all: p.texture } } : p;
          }),
        }
        : fields;
    }
    out.mobs = next;
  }
  return out;
}
